import { useMemo } from 'react'

import { useQuery } from '@apollo/client'
import { IconBulldozer } from '@tabler/icons-react'
import { Empty } from 'antd'
import Link from 'next/link'
import { useRouter } from 'next/router'
import styled, { keyframes } from 'styled-components'

import { Button } from '../shared-components/BUI/Button'
import { Text } from '../shared-components/BUI/Text'

import { accountManagers } from '@/config/constants/roleGroups'
import { useProject } from '@/context/projectContext'
import { GET_ADMIN_USERS } from '@/modules/adminUsers/graphQL'
import { Role } from '@/modules/adminUsers/roles'
import { ProjectContactsList } from '@/modules/projects/ProjectContactsList'
import { useRoles } from '@/utils/ensureRole'
import { getAdminUserName } from '@/utils/getName'
import { routes } from '@/utils/routes'

import {
  AdminUsersUiQuery,
  AdminUsersUiQueryVariables,
  ContactType,
} from 'types/api.graphql'

const BackAndForth = keyframes`
  0%,
  100% {
    transform: translateX(0) rotateY(0);
  }
  20% {
    transform: translateX(50px) rotateY(0);
  }
  30% {
    transform: translateX(50px) rotateY(180deg);
  }
  50% {
    transform: translateX(0) rotateY(180deg);
  }
  70% {
    transform: translateX(-50px) rotateY(180deg);
  }
  85% {
    transform: translateX(-50px) rotateY(0);
  }
`

const Wrapper = styled.div`
  margin-top: 10vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;

  & .dozerLoader {
    color: ${({ theme }) => theme.colour.secondary._300};
    animation: ${BackAndForth} 3s linear infinite;
  }
`

type Props = {
  children?: React.ReactNode
}

export const CorePageNoProjectPrompt: React.FC<Props> = ({ children }) => {
  const { pathname } = useRouter()
  const { hasProjects, projectsLoading } = useProject()
  const { hasRole } = useRoles()

  const allowsNoProject = useMemo(() => {
    return (
      pathname.startsWith('/account') ||
      pathname.startsWith('/admin') ||
      pathname.startsWith('/super') ||
      pathname.startsWith('/bui') ||
      pathname.startsWith('/registers') ||
      pathname.startsWith('/subcontractors')
    )
  }, [pathname])

  if (hasProjects || allowsNoProject) {
    return <>{children}</>
  }

  if (projectsLoading) return null

  if (hasRole(accountManagers)) {
    return (
      <Wrapper>
        <Empty
          image={<IconBulldozer size={64} className="dozerLoader" />}
          description={
            <>
              <Text variant="display01" block mb="1rem" secondary="_500">
                No active projects
              </Text>
              Looks like you haven&apos;t setup any projects yet. <br />
              Return back to this page after you&apos;ve made your first
              project.
            </>
          }
        />
        <Link href={routes.projects.new()} passHref>
          <Button variant="primary">Create a project</Button>
        </Link>
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <Empty
        image={null}
        description={
          <>
            <Text variant="display01" block mb="1rem" secondary="_500">
              No assigned projects
            </Text>
            <br />
            Looks like you haven&apos;t been assigned to any projects yet.
            <br />
            Reach out to one of the following people to help you out
          </>
        }
      />
      <br />
      <OwnersAndManagers />
    </Wrapper>
  )
}

const OwnersAndManagers = () => {
  const { data, loading } = useQuery<
    AdminUsersUiQuery,
    AdminUsersUiQueryVariables
  >(GET_ADMIN_USERS, {
    variables: {
      filter: {
        roles: [Role.BuilderOwner, Role.BuilderManager],
        excludeRoles: [Role.SuperAdmin, Role.BpSuperAdmin],
      },
    },
  })

  if (loading) {
    return <>...one second</>
  }

  return (
    <ProjectContactsList
      contacts={data.adminUsers?.map((admin) => ({
        id: admin.id,
        type: ContactType.Other,
        customType: admin.roles.map((r) => r.name).includes(Role.BuilderOwner)
          ? 'Owner'
          : 'Manager',
        name: getAdminUserName(admin),
        email: admin.user.email,
      }))}
    />
  )
}
